import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Spin, Empty, message } from "antd";
import { DeleteOutlined, LoadingOutlined } from "@ant-design/icons";
import { uploadHeroProductImage } from "../../../../utils/heroUpload";
import { convertToJpeg } from "../../../../utils/convertToJpeg";
import { useHeroPublicLibraryImages } from "./useHeroPublicLibraryImages";
import HeroTagInput from "./HeroTagInput";

const UNIT_OPTIONS = ["in", "cm", "mm", "m", "ft"];

const UNIT_LONG_NAME = {
    in: "inches",
    cm: "centimeters",
    mm: "millimeters",
    m: "meters",
    ft: "feet",
};

const UNIT_SHORT_NAME = {
    inches: "in",
    centimeters: "cm",
    millimeters: "mm",
    meters: "m",
    feet: "ft",
};

const ACCEPTED_UPLOAD_TYPES = "image/jpeg,image/png,image/webp,image/heic,image/tiff,image/avif";
const MAX_PRODUCT_IMAGES = 6;

const MODE_CONFIG = {
    product: {
        title: "Add Product Photos",
        dropHelp: "Clean backgrounds preferred.",
        showDimensions: true,
        addMoreLabel: "Add more",
        emptyLabel: "Add photos",
        namePlaceholder: "Enter product name",
        commitVerb: "Add",
        multi: true,
        requireName: true,
        showPrimaryAngle: true,
    },
    inspiration: {
        title: "Add Inspiration",
        dropHelp: "A single reference image works best.",
        showDimensions: false,
        addMoreLabel: "Replace",
        emptyLabel: "Add reference",
        namePlaceholder: "Enter inspiration name",
        commitVerb: "Add",
        multi: false,
        // Inspiration is optional metadata — name isn't required.
        requireName: false,
        // Single-file mode, never has a primary-angle picker.
        showPrimaryAngle: false,
    },
    prop: {
        title: "Add Prop",
        dropHelp: "Props are supporting items in the scene.",
        showDimensions: true,
        addMoreLabel: "Add more",
        emptyLabel: "Add photos",
        namePlaceholder: "Enter prop name",
        commitVerb: "Add",
        multi: true,
        requireName: true,
        // Per portal UX: primary-angle picker is product-only.
        showPrimaryAngle: false,
    },
};

const positiveNumber = (v) => {
    const n = parseFloat(String(v ?? "").trim());
    return Number.isFinite(n) && n > 0 ? n : null;
};

// Block characters that `<input type="number">` still allows but we don't want
// in dimension fields: sign chars and exponent notation.
const blockNonNumericKeys = (e) => {
    if (["-", "+", "e", "E"].includes(e.key)) e.preventDefault();
};

// Strip anything that isn't digits or a single decimal point from pasted text.
const sanitizePastedNumber = (text) => {
    const cleaned = text.replace(/[^0-9.]/g, "");
    const firstDot = cleaned.indexOf(".");
    if (firstDot === -1) return cleaned;
    return cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, "");
};

const seedFromCommitted = (committed) => {
    if (!committed) return null;
    let tagsArr = [];
    if (Array.isArray(committed.tags)) {
        tagsArr = committed.tags.filter(Boolean);
    } else if (typeof committed.tags === "string" && committed.tags.trim()) {
        // Legacy seed shape (comma-separated string) — split for back-compat.
        tagsArr = committed.tags.split(",").map((t) => t.trim()).filter(Boolean);
    }
    return {
        uploads: committed.uploads || [],
        primaryFileUid: committed.primaryFileUid || null,
        width: committed.width,
        height: committed.height,
        depth: committed.depth,
        unit: UNIT_SHORT_NAME[committed.unit] || committed.unit || "in",
        name: committed.name,
        tags: tagsArr,
    };
};

const AddAssetModal = ({
    open,
    mode = "product",
    title,
    committed = null, // existing committed state to seed (only for re-opening)
    onCancel,
    onSubmit,
    backdropZIndex = 6000,
    // ALL3D Library tab only makes sense for actual inspiration uploads. The
    // hero reuses this modal for variant "material reference" — in that case
    // the parent passes allowAll3dLibrary={false} so the tab is hidden.
    allowAll3dLibrary = true,
    // Multi-prop add-another flow (mirrors portal CreateProductModal). When
    // `onAddAnother` is provided and `mode === "prop"`, an "Add another" button
    // appears in the footer + a "{currentPropCount}/{maxProps}" pill in the
    // header. Clicking it commits the current prop and resets the form without
    // closing the modal.
    currentPropCount = 0,
    maxProps = 3,
    onAddAnother,
}) => {
    const cfg = MODE_CONFIG[mode] || MODE_CONFIG.product;

    const showAll3dLibraryTab = mode === "inspiration" && allowAll3dLibrary !== false;

    const [tab, setTab] = useState("upload"); // 'upload' | 'library' | 'all3dLibrary'
    const [uploads, setUploads] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [primaryFileUid, setPrimaryFileUid] = useState(null);
    const [width, setWidth] = useState("");
    const [height, setHeight] = useState("");
    const [depth, setDepth] = useState("");
    const [unit, setUnit] = useState("in");
    const [name, setName] = useState("");
    const [tags, setTags] = useState([]); // string[] (chips), not a comma-separated string

    const [nameError, setNameError] = useState("");
    const [dimensionErrors, setDimensionErrors] = useState({});
    const [primaryError, setPrimaryError] = useState(false);

    // ALL3D Library tab — public inspiration feed (mirrors portal CreateProductModal)
    const [selectedLibraryImage, setSelectedLibraryImage] = useState(null);
    const libraryScrollRef = useRef(null);
    const {
        images: libraryImages,
        loading: libraryLoading,
        loadingMore: libraryLoadingMore,
        hasMore: libraryHasMore,
        loadMore: libraryLoadMore,
    } = useHeroPublicLibraryImages(open && showAll3dLibraryTab && tab === "all3dLibrary");

    const handleLibraryScroll = useCallback(() => {
        const el = libraryScrollRef.current;
        if (!el || libraryLoadingMore || !libraryHasMore) return;
        // Within 200px of the bottom — fetch next page
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) libraryLoadMore();
    }, [libraryLoadingMore, libraryHasMore, libraryLoadMore]);

    const fileInputRef = useRef(null);

    useEffect(() => {
        if (open) {
            message.config({ top: 24 });
        }
    }, [open]);

    // Reusable reset — used both when the modal opens and after "Add another"
    // commits a prop (keeps the modal open with a clean form for the next entry).
    const applyReset = (seed) => {
        setUploads(seed?.uploads || []);
        setPrimaryFileUid(seed?.primaryFileUid || null);
        setWidth(seed?.width != null ? String(seed.width) : "");
        setHeight(seed?.height != null ? String(seed.height) : "");
        setDepth(seed?.depth != null ? String(seed.depth) : "");
        setUnit(seed?.unit || "in");
        setName(seed?.name || "");
        setTags(Array.isArray(seed?.tags) ? seed.tags : []);
        setNameError("");
        setDimensionErrors({});
        setPrimaryError(false);
        setIsUploading(false);
        setSelectedLibraryImage(null);
    };

    // Reset (or seed from `committed`) every time the modal opens.
    useEffect(() => {
        if (!open) return;
        const seed = seedFromCommitted(committed);
        setTab("upload");
        applyReset(seed);
    }, [open, mode, committed]); // eslint-disable-line react-hooks/exhaustive-deps

    const triggerFilePicker = () => fileInputRef.current && fileInputRef.current.click();

    const startUploads = async (rawFiles) => {
        const arr = Array.from(rawFiles || []).filter((f) => f && f.type && f.type.startsWith("image/"));
        if (arr.length === 0) return;
        let trimmed;
        if (!cfg.multi) {
            trimmed = arr.slice(0, 1);
        } else if (mode === "product") {
            const existing = uploads.filter((u) => u.status !== "error").length;
            const remaining = MAX_PRODUCT_IMAGES - existing;
            if (remaining <= 0) {
                message.warning(`Maximum ${MAX_PRODUCT_IMAGES} product images allowed.`);
                return;
            }
            if (arr.length > remaining) {
                message.warning(`Only ${MAX_PRODUCT_IMAGES} images are allowed. Using the first ${remaining}.`);
                trimmed = arr.slice(0, remaining);
            } else {
                trimmed = arr;
            }
        } else {
            trimmed = arr;
        }

        // Convert HEIC / TIFF to JPEG up front so browser previews work
        // (URL.createObjectURL can't render those formats) and S3 ends up with a
        // universally-viewable file. Non-HEIC/TIFF passes through untouched.
        let converted;
        try {
            converted = await Promise.all(trimmed.map((f) => convertToJpeg(f)));
        } catch (err) {
            console.error("Image conversion failed:", err);
            message.error("Failed to convert image. Please try a different file.");
            return;
        }

        const pending = converted.map((f) => ({
            uid: `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            name: f.name,
            image_uri: "",
            url: URL.createObjectURL(f),
            status: "uploading",
            _file: f,
        }));

        if (cfg.multi) {
            setUploads((prev) => [...prev, ...pending]);
        } else {
            setUploads(pending); // replace for single-file modes (inspiration)
            setPrimaryFileUid(null);
        }
        setIsUploading(true);

        try {
            await Promise.all(
                pending.map(async (entry) => {
                    try {
                        const result = await uploadHeroProductImage(entry._file);
                        setUploads((prev) =>
                            prev.map((u) =>
                                u.uid === entry.uid
                                    ? { uid: result.uid, name: result.name, image_uri: result.image_uri, url: result.url, status: "done" }
                                    : u
                            )
                        );
                    } catch (err) {
                        console.error("Hero asset upload failed:", err);
                        setUploads((prev) =>
                            prev.map((u) => (u.uid === entry.uid ? { ...u, status: "error" } : u))
                        );
                        message.error(`Failed to upload ${entry.name}`);
                    }
                })
            );
        } finally {
            setIsUploading(false);
        }
    };

    const handleFilesPicked = (e) => {
        startUploads(e.target.files);
        e.target.value = "";
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer?.files?.length) startUploads(e.dataTransfer.files);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleRemoveUpload = (uid) => {
        setUploads((prev) => prev.filter((u) => u.uid !== uid));
        if (primaryFileUid === uid) setPrimaryFileUid(null);
    };

    const handlePickPrimary = (uid) => {
        setPrimaryFileUid(uid);
        if (primaryError) setPrimaryError(false);
    };

    const handleConfirm = () => {
        // ALL3D Library flow (inspiration only): submit the picked image without
        // upload / name / dimensions validation. Mirrors portal CreateProductModal
        // lines 731–752.
        if (showAll3dLibraryTab && tab === "all3dLibrary") {
            if (!selectedLibraryImage) {
                message.error("Please select an inspiration from ALL3D Library.");
                return;
            }
            const lib = selectedLibraryImage;
            onSubmit({
                mode,
                source: "all3d_library",
                uploads: [{
                    uid: String(lib.inspiration_id || lib.id || lib.originalUrl),
                    name: lib.display_name || "ALL3D Inspiration",
                    image_uri: lib.originalUrl,
                    url: lib.thumbnail || lib.src || lib.originalUrl,
                    status: "done",
                    library: {
                        inspiration_id: lib.inspiration_id,
                        thumbnail: lib.thumbnail,
                        originalUrl: lib.originalUrl,
                    },
                }],
                primaryFileUid: String(lib.inspiration_id || lib.id || lib.originalUrl),
                name: lib.display_name || "ALL3D Inspiration",
                tags: [],
                width: null,
                height: null,
                depth: null,
                unit: null,
            });
            return;
        }

        const built = buildValidatedResult();
        if (!built) return;
        onSubmit(built);
    };

    // Runs the upload / name / dimensions / primary-angle validations and either
    // returns a result-object ready to emit, or null (after setting the
    // appropriate inline error state and surfacing a toast).
    const buildValidatedResult = () => {
        if (isUploading) {
            message.warning("Please wait for the upload to complete.");
            return null;
        }
        const errored = uploads.find((u) => u.status === "error");
        if (errored) {
            message.error("Please fix upload errors before proceeding.");
            return null;
        }
        const done = uploads.filter((u) => u.status === "done");
        if (done.length === 0) {
            message.warning("Please upload at least one photo.");
            return null;
        }
        const trimmedName = name.trim();
        if (cfg.requireName !== false && !trimmedName) {
            setNameError("Name is required");
            return null;
        }
        if (cfg.showDimensions) {
            const dimErr = {};
            if (positiveNumber(width) == null) dimErr.width = "Required";
            if (positiveNumber(height) == null) dimErr.height = "Required";
            if (positiveNumber(depth) == null) dimErr.depth = "Required";
            if (Object.keys(dimErr).length > 0) {
                setDimensionErrors(dimErr);
                return null;
            }
            setDimensionErrors({});
        }
        let resolvedPrimary = primaryFileUid;
        if (done.length === 1) {
            resolvedPrimary = done[0].uid;
        } else if (cfg.showPrimaryAngle !== false && (!resolvedPrimary || !done.some((u) => u.uid === resolvedPrimary))) {
            setPrimaryError(true);
            return null;
        }
        setPrimaryError(false);
        const tagList = Array.isArray(tags) ? tags : [];
        return {
            mode,
            uploads: done,
            primaryFileUid: resolvedPrimary || done[0].uid,
            name: trimmedName,
            tags: tagList,
            width: cfg.showDimensions ? positiveNumber(width) : null,
            height: cfg.showDimensions ? positiveNumber(height) : null,
            depth: cfg.showDimensions ? positiveNumber(depth) : null,
            unit: UNIT_LONG_NAME[unit] || "inches",
        };
    };

    // "Add another" — commit the current prop and reset for the next one,
    // keeping the modal open. Mirrors portal handleAddAnotherProp.
    const handleAddAnother = () => {
        if (!onAddAnother) return;
        const built = buildValidatedResult();
        if (!built) return;
        onAddAnother(built);
        applyReset(null); // fresh form for the next prop
    };

    const doneCount = uploads.filter((u) => u.status === "done").length;
    const productSlotsRemaining =
        mode === "product"
            ? MAX_PRODUCT_IMAGES - uploads.filter((u) => u.status !== "error").length
            : Infinity;
    const canAddMore = cfg.multi ? productSlotsRemaining > 0 : uploads.length === 0;
    // Primary-angle picker is product-only (portal parity). Even though props
    // are multi-upload, the portal lets the user just pick any image and doesn't
    // require an explicit primary; inspiration is single-file so it's moot.
    const showPrimaryPicker = cfg.showPrimaryAngle !== false && cfg.multi && uploads.length > 1;

    return (
        <Modal
            visible={open}
            onCancel={onCancel}
            footer={null}
            centered
            width={560}
            wrapClassName="lv2-product-modal-wrap"
            title={null}
            closable={false}
            destroyOnClose
            zIndex={backdropZIndex}
            maskStyle={{ background: "rgba(0, 0, 0, 0.65)", backdropFilter: "blur(2px)", WebkitBackdropFilter: "blur(2px)" }}
        >
            <div className="lv2-pm">
                <div className="lv2-pm-header">
                    <h3 className="manrope f-18 w-700 lh-28">
                        <span>{title ?? cfg.title}</span>
                        {mode === "prop" && (
                            <span className="lv2-pm-header-count manrope f-12 w-500">
                                {currentPropCount}/{maxProps}
                            </span>
                        )}
                    </h3>
                    <button type="button" className="lv2-pm-close" aria-label="Close" onClick={onCancel}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                    </button>
                </div>

                <div className="lv2-pm-tabs">
                    <button type="button" className={`lv2-pm-tab manrope f-14 w-500 ${tab === "upload" ? "active" : ""}`} onClick={() => setTab("upload")}>
                        Upload
                    </button>
                    {showAll3dLibraryTab && (
                        <button type="button" className={`lv2-pm-tab manrope f-14 w-500 ${tab === "all3dLibrary" ? "active" : ""}`} onClick={() => setTab("all3dLibrary")}>
                            ALL3D Library
                        </button>
                    )}
                    <button type="button" className={`lv2-pm-tab manrope f-14 w-500 ${tab === "library" ? "active" : ""}`} onClick={() => setTab("library")}>
                        From Library
                    </button>
                </div>

                {tab === "upload" ? (
                    <>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept={ACCEPTED_UPLOAD_TYPES}
                            multiple={cfg.multi}
                            style={{ display: "none" }}
                            onChange={handleFilesPicked}
                        />

                        {uploads.length === 0 ? (
                            <div
                                className="lv2-pm-dropzone"
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                                onClick={triggerFilePicker}
                                role="button"
                                tabIndex={0}
                            >
                                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#91D5FF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                    <polyline points="17 8 12 3 7 8"/>
                                    <line x1="12" y1="3" x2="12" y2="15"/>
                                </svg>
                                <div className="manrope f-14 w-500 lh-22 lv2-pm-drop-line">
                                    <span>Drag &amp; Drop or </span>
                                    <button type="button" className="lv2-pm-upload-link" onClick={(e) => { e.stopPropagation(); triggerFilePicker(); }}>
                                        Upload photos
                                    </button>
                                </div>
                                <div className="manrope f-12 w-400 lh-20 lv2-pm-drop-sub">{cfg.dropHelp}</div>
                            </div>
                        ) : (
                            <>
                                {showPrimaryPicker && (
                                    <div className={`lv2-pm-primary-banner ${primaryError ? "is-error" : ""}`}>
                                        <div className="manrope f-13 w-700 lv2-pm-primary-heading">
                                            {primaryError ? "Primary angle required" : "Set your primary angle"}
                                        </div>
                                        <div className="manrope f-12 w-400 lh-20 lv2-pm-primary-helper">
                                            {primaryError
                                                ? "Click one of the photos below to set it as the primary angle before continuing."
                                                : primaryFileUid
                                                ? "The highlighted photo is your primary angle. Click any other photo to change it."
                                                : "Click a photo to set it as the primary angle — AI will prefer it as the main product view."}
                                        </div>
                                    </div>
                                )}

                                <div className="lv2-pm-grid" onDrop={handleDrop} onDragOver={handleDragOver}>
                                    {uploads.map((u) => {
                                        const isPrimary = showPrimaryPicker && primaryFileUid === u.uid;
                                        const clickable = showPrimaryPicker && u.status === "done";
                                        return (
                                            <div
                                                key={u.uid}
                                                className={`lv2-pm-grid-card lv2-pm-grid-${u.status} ${isPrimary ? "is-primary" : ""} ${clickable ? "is-clickable" : ""}`}
                                                onClick={clickable ? () => handlePickPrimary(u.uid) : undefined}
                                                role={clickable ? "button" : undefined}
                                            >
                                                {u.url ? <img src={u.url} alt={u.name} /> : null}
                                                {u.status === "uploading" && (
                                                    <div className="lv2-pm-grid-overlay">
                                                        <Spin indicator={<LoadingOutlined style={{ fontSize: 22, color: "#ffffff" }} spin />} />
                                                    </div>
                                                )}
                                                <button
                                                    type="button"
                                                    className="lv2-pm-grid-remove"
                                                    aria-label="Remove"
                                                    onClick={(e) => { e.stopPropagation(); handleRemoveUpload(u.uid); }}
                                                >
                                                    <DeleteOutlined style={{ color: "#ffffff", fontSize: 12 }} />
                                                </button>
                                                {isPrimary && (
                                                    <div className="manrope f-12 w-500 lv2-pm-grid-primary-badge">✓ Primary</div>
                                                )}
                                            </div>
                                        );
                                    })}

                                    {canAddMore && (
                                        <button type="button" className="lv2-pm-grid-add manrope f-11 w-500" onClick={triggerFilePicker}>
                                            <span className="lv2-pm-grid-add-plus">
                                                <svg width="16" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M8.78558 1.57227H7.71415C7.61891 1.57227 7.57129 1.61988 7.57129 1.71512V7.32227H2.25028C2.15504 7.32227 2.10742 7.36988 2.10742 7.46512V8.53655C2.10742 8.63179 2.15504 8.67941 2.25028 8.67941H7.57129V14.2866C7.57129 14.3818 7.61891 14.4294 7.71415 14.4294H8.78558C8.88081 14.4294 8.92843 14.3818 8.92843 14.2866V8.67941H14.2503C14.3455 8.67941 14.3931 8.63179 14.3931 8.53655V7.46512C14.3931 7.36988 14.3455 7.32227 14.2503 7.32227H8.92843V1.71512C8.92843 1.61988 8.88081 1.57227 8.78558 1.57227Z" fill="currentColor"/>
                                                </svg>
                                            </span>
                                            <span>{doneCount === 0 ? cfg.emptyLabel : cfg.addMoreLabel}</span>
                                        </button>
                                    )}
                                </div>
                            </>
                        )}

                        <div className="manrope f-12 w-400 lh-20 lv2-pm-supported">
                            Supported: JPG, PNG, WebP, HEIC, TIFF, AVIF.
                        </div>

                        <div className="lv2-pm-section">
                            <label className="manrope f-13 w-600 lv2-pm-label">
                                Name {cfg.requireName !== false
                                    ? <span className="lv2-pm-required">*</span>
                                    : <span className="lv2-pm-optional">(optional)</span>}
                            </label>
                            <input
                                type="text"
                                className={`lv2-pm-input manrope f-13 w-400 ${nameError ? "has-error" : ""}`}
                                placeholder={cfg.namePlaceholder}
                                value={name}
                                onChange={(e) => {
                                    setName(e.target.value);
                                    if (nameError && e.target.value.trim()) setNameError("");
                                }}
                            />
                            {nameError && <div className="lv2-pm-field-error manrope f-12 w-500">{nameError}</div>}
                        </div>

                        {cfg.showDimensions && (
                            <div className="lv2-pm-section">
                                <label className="manrope f-13 w-600 lv2-pm-label">
                                    Dimensions <span className="lv2-pm-required">*</span>
                                </label>
                                <div className="lv2-pm-dim-row">
                                    <input
                                        type="number"
                                        inputMode="decimal"
                                        min="0"
                                        className={`lv2-pm-input manrope f-13 w-400 ${dimensionErrors.width ? "has-error" : ""}`}
                                        placeholder="Width"
                                        value={width}
                                        onKeyDown={blockNonNumericKeys}
                                        onPaste={(e) => {
                                            e.preventDefault();
                                            const cleaned = sanitizePastedNumber(e.clipboardData.getData("text"));
                                            setWidth(cleaned);
                                            if (dimensionErrors.width) setDimensionErrors((p) => ({ ...p, width: undefined }));
                                        }}
                                        onChange={(e) => { setWidth(e.target.value); if (dimensionErrors.width) setDimensionErrors((p) => ({ ...p, width: undefined })); }}
                                    />
                                    <input
                                        type="number"
                                        inputMode="decimal"
                                        min="0"
                                        className={`lv2-pm-input manrope f-13 w-400 ${dimensionErrors.height ? "has-error" : ""}`}
                                        placeholder="Height"
                                        value={height}
                                        onKeyDown={blockNonNumericKeys}
                                        onPaste={(e) => {
                                            e.preventDefault();
                                            const cleaned = sanitizePastedNumber(e.clipboardData.getData("text"));
                                            setHeight(cleaned);
                                            if (dimensionErrors.height) setDimensionErrors((p) => ({ ...p, height: undefined }));
                                        }}
                                        onChange={(e) => { setHeight(e.target.value); if (dimensionErrors.height) setDimensionErrors((p) => ({ ...p, height: undefined })); }}
                                    />
                                    <input
                                        type="number"
                                        inputMode="decimal"
                                        min="0"
                                        className={`lv2-pm-input manrope f-13 w-400 ${dimensionErrors.depth ? "has-error" : ""}`}
                                        placeholder="Depth"
                                        value={depth}
                                        onKeyDown={blockNonNumericKeys}
                                        onPaste={(e) => {
                                            e.preventDefault();
                                            const cleaned = sanitizePastedNumber(e.clipboardData.getData("text"));
                                            setDepth(cleaned);
                                            if (dimensionErrors.depth) setDimensionErrors((p) => ({ ...p, depth: undefined }));
                                        }}
                                        onChange={(e) => { setDepth(e.target.value); if (dimensionErrors.depth) setDimensionErrors((p) => ({ ...p, depth: undefined })); }}
                                    />
                                    <select className="lv2-pm-unit manrope f-13 w-500" value={unit} onChange={(e) => setUnit(e.target.value)}>
                                        {UNIT_OPTIONS.map((u) => (
                                            <option key={u} value={u}>{u}</option>
                                        ))}
                                    </select>
                                </div>
                                {(dimensionErrors.width || dimensionErrors.height || dimensionErrors.depth) && (
                                    <div className="lv2-pm-field-error manrope f-12 w-500">Width, height and depth are required.</div>
                                )}
                            </div>
                        )}

                        <div className="lv2-pm-section">
                            <label className="manrope f-13 w-600 lv2-pm-label">
                                Tags <span className="lv2-pm-optional">(optional)</span>
                            </label>
                            <HeroTagInput
                                value={tags}
                                onChange={setTags}
                                placeholder="Type a tag and press Enter"
                            />
                        </div>
                    </>
                ) : tab === "all3dLibrary" && showAll3dLibraryTab ? (
                    <div className="lv2-pm-all3d">
                        <div ref={libraryScrollRef} onScroll={handleLibraryScroll} className="lv2-pm-all3d-scroll">
                            {libraryLoading ? (
                                <div className="lv2-pm-all3d-loading">
                                    <Spin indicator={<LoadingOutlined style={{ color: "#91D5FF" }} spin />} />
                                </div>
                            ) : libraryImages.length === 0 ? (
                                <Empty description={<span style={{ color: "rgba(255,255,255,0.55)" }}>No images found.</span>} />
                            ) : (
                                <>
                                    <div className="lv2-pm-all3d-grid">
                                        {libraryImages.map((img, idx) => {
                                            const isSelected = selectedLibraryImage?.id === img.id && img.id;
                                            return (
                                                <div
                                                    key={img.id || `all3d-${idx}`}
                                                    className={`lv2-pm-all3d-card ${isSelected ? "is-selected" : ""}`}
                                                    onClick={() => setSelectedLibraryImage(isSelected ? null : img)}
                                                >
                                                    <img src={img.thumbnail} alt={img.display_name || "Inspiration"} loading="lazy" />
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {libraryLoadingMore && (
                                        <div className="lv2-pm-all3d-loading">
                                            <Spin indicator={<LoadingOutlined style={{ color: "#91D5FF" }} spin />} />
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="lv2-pm-library-empty manrope f-13 w-500">
                        Sign up to browse your saved {mode === "inspiration" ? "inspirations" : mode === "prop" ? "props" : "products"}.
                    </div>
                )}

                <div className="lv2-pm-actions">
                    <button type="button" className="lv2-pm-cancel manrope f-14 w-500" onClick={onCancel}>
                        Cancel
                    </button>
                    {mode === "prop" && onAddAnother && (() => {
                        // Show only when adding *this* prop wouldn't be the last one
                        // — i.e. there's room for at least one more after this commit.
                        const propsAfterAdding = currentPropCount + 1;
                        const canAddAnother = propsAfterAdding < maxProps;
                        return (
                            <button
                                type="button"
                                className="lv2-pm-add-another manrope f-14 w-600"
                                onClick={handleAddAnother}
                                disabled={!canAddAnother}
                                title={!canAddAnother ? `Maximum ${maxProps} props allowed` : undefined}
                            >
                                Add another
                            </button>
                        );
                    })()}
                    <button type="button" className="lv2-pm-add manrope f-14 w-600" onClick={handleConfirm}>
                        {cfg.commitVerb}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default AddAssetModal;
